import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UpdateClassificationRequest, ValidatorService } from '~/services/validator.service';

export type DataTableRowAction = 'approve' | 'reject';

export interface DataTableActionEvent {
  action: DataTableRowAction;
  row: Record<string, unknown>;
}

export interface DataTableColumn<T = Record<string, unknown>> {
  key: keyof T | string;
  header: string;
  align?: 'left' | 'center' | 'right';
}
type TextAlign = 'left' | 'center' | 'right';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: false
})
export class DataTableComponent implements OnChanges {
  @Input() columns: DataTableColumn[] = [];
  @Input() rows: Record<string, unknown>[] = [];
  @Input() paginateRows: boolean = false;
  @Input() pageSize: number = 10;
  /** Key whose cell value renders as a clickable link. */
  @Input() clickableKey: string = '';
  /** Key whose cell renders approve/reject action buttons. */
  @Input() actionKey: string = '';
  /** Key whose cell renders a single button (e.g., View Image). */
  @Input() singleActionKey: string = '';
  @Input() singleActionLabel: string = 'View';
  /** Optional second single action key for tables needing two single-action columns. */
  @Input() secondarySingleActionKey: string = '';
  @Input() secondarySingleActionLabel: string = 'Approve';
  /** Function to dynamically determine the secondary action button label per row. */
  @Input() secondarySingleActionLabelFn: ((row: Record<string, unknown>) => string) | null = null;
  /** Accordion config for expandable child table rows. */
  @Input() accordionKey: string = '';
  @Input() accordionRowKey: string = '';
  @Input() childColumns: DataTableColumn[] = [];
  @Input() childRowsByParentKey: Record<string, Record<string, unknown>[]> = {};
  @Input() childSingleActionKey: string = '';
  @Input() childSingleActionLabel: string = 'View';
  @Input() childSecondarySingleActionKey: string = '';
  @Input() childSecondarySingleActionLabel: string = 'Action';
  /** Function to dynamically determine the child secondary action button label per row. */
  @Input() childSecondarySingleActionLabelFn: ((row: Record<string, unknown>) => string) | null = null;
  @Input() childPageSize: number = 10;

  @Output() rowClick = new EventEmitter<Record<string, unknown>>();
  @Output() actionClick = new EventEmitter<DataTableActionEvent>();
  @Output() singleActionClick = new EventEmitter<Record<string, unknown>>();
  @Output() secondarySingleActionClick = new EventEmitter<Record<string, unknown>>();
  @Output() refreshData = new EventEmitter<boolean>();

  public currentPage: number = 1;
  private viewMoreState: Record<string, boolean> = {};
  // Track which rows have classification changed (by rowIndex)
  private rowModifiedState: Record<number, boolean> = {};

  public classificationList = [
    { name: 'Critical', value: 'critical' },
    { name: 'Not Critical', value: 'not_critical' },
  ];
  private duplicateChildRowsByParentKey: Record<string, Record<string, unknown>[]> = {};
  private readonly expandedRowKeys = new Set<string>();

  constructor(private readonly validatorService: ValidatorService) {

  }

  /**
   * When input properties change, we need to reset the duplicateChildRowsByParentKey to reflect the new data and also reset any modified state tracking since it's tied to the specific data set. This ensures that the component correctly reflects the current state of the data and that any user interactions are accurately tracked against the right set of rows.
   */
  ngOnChanges(): void {
    this.duplicateChildRowsByParentKey = structuredClone(this.childRowsByParentKey);
    // Reset modified state when data changes
    this.rowModifiedState = {};
  }

  /**
   * Returns the dynamic label for the secondary action button per row.
   * If a labelFn is provided, it uses that; otherwise falls back to the static label.
   */
  public getSecondaryActionLabel(row: Record<string, unknown>, index: number): string {
    if (this.secondarySingleActionLabelFn) {
      return this.secondarySingleActionLabelFn(row);
    }
    return this.getChildRows(row)[index].validation_status ? String(this.getChildRows(row)[index].validation_status).toUpperCase() : this.secondarySingleActionLabel;
  }

  public get normalizedPageSize(): number {
    return Math.max(1, this.pageSize || 1);
  }

  public get totalRows(): number {
    return this.rows.length;
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRows / this.normalizedPageSize));
  }

  public get pagedRows(): Record<string, unknown>[] {
    const start = (this.currentPage - 1) * this.normalizedPageSize;
    return this.rows.slice(start, start + this.normalizedPageSize);
  }

  public get displayedRows(): Record<string, unknown>[] {
    return this.paginateRows ? this.pagedRows : this.rows;
  }

  public get shouldShowPagination(): boolean {
    return this.paginateRows && this.totalRows > this.normalizedPageSize;
  }

  public get startRecordIndex(): number {
    if (this.totalRows === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.normalizedPageSize + 1;
  }

  public get endRecordIndex(): number {
    return Math.min(this.currentPage * this.normalizedPageSize, this.totalRows);
  }

  public getCellValue(row: Record<string, unknown>, key: string | keyof Record<string, unknown>): string | number {
    const value = row[key];
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (key === 'view_image') {
      return '';
    }

    if (typeof value === 'number' || typeof value === 'string') {
      return value;
    }
    return String(value);
  }


  public getTextAlign(align?: TextAlign): TextAlign {
    return align ?? 'left';
  }

  public getHeaderTextAlign(): 'left' {
    return 'left';
  }

  public isClickable(key: string | keyof Record<string, unknown>): boolean {
    return this.clickableKey !== '' && key === this.clickableKey;
  }

  public isActionColumn(key: string | keyof Record<string, unknown>): boolean {
    return this.actionKey !== '' && key === this.actionKey;
  }

  public isSingleActionColumn(key: string | keyof Record<string, unknown>): boolean {
    return this.singleActionKey !== '' && key === 'view_image';
  }

  public isSecondarySingleActionColumn(key: string | keyof Record<string, unknown>): boolean {
    return this.secondarySingleActionKey !== '' && key === this.secondarySingleActionKey;
  }

  public isAccordionColumn(key: string | keyof Record<string, unknown>): boolean {
    return this.accordionKey !== '' && key === this.accordionKey;
  }

  public onCellClick(row: Record<string, unknown>, key: string | keyof Record<string, unknown>): void {
    if (this.isClickable(key)) {
      this.rowClick.emit(row);
    }
  }

  public onActionButtonClick(
    event: MouseEvent,
    row: Record<string, unknown>,
    action: DataTableRowAction
  ): void {
    event.stopPropagation();
    this.actionClick.emit({ action, row });
  }

  public onSingleActionButtonClick(event: MouseEvent, row: Record<string, unknown>): void {
    event.stopPropagation();
    this.singleActionClick.emit(row);
  }

  public onSecondarySingleActionButtonClick(row: Record<string, unknown>, index: number): void {
    const documentId = this.displayedRows[index]?.document_id;
    const recordId = this.getChildRows(row)[0].record_id;
    const childRows = this.getChildRows(row);
    const childRowsArray = Object.values(this.duplicateChildRowsByParentKey).flat();

    const validations = childRows.map((childRow) => {
      const matchingRow = childRowsArray.find(
        (row) => row.statementId === childRow.statementId
      );

      if (matchingRow) {
        // classification, validator_reason
        const isClassificationDifferent = childRow.classification !== matchingRow.classification;
        if (isClassificationDifferent) {
          return {
            statementId: childRow.statementId,
            status: 'approved',
            validator_classification: childRow.classification,
            reason: childRow.validator_reason,
          };
        } else {
          return {
            statementId: childRow.statementId,
            status: "approved",
          };
        }
      } else {
        return {
          statementId: childRow.statementId,
          status: "approved"
        };
      }
    });

    const payload: UpdateClassificationRequest = {
      validations: validations.map(v => ({
        statementId: String(v.statementId),
        status: v.status,
        validator_classification: v.validator_classification ? String(v?.validator_classification) : undefined,
        reason: v.reason ? String(v?.reason) : undefined,
      }))
    };

    const queryParams = {
      record_id: String(recordId),
      document_id: String(documentId)
    };

    this.validatorService.updateClassification(queryParams, payload).subscribe({
      next: (response) => {
        if (response) {
          this.refreshData.emit(true);
        }
      },
      error: (error) => {
        console.error('Error updating classification:', error);
      }
    });

  }

  public onClassificationChange(event: Event, row: Record<string, unknown>, rowIndex: number): void {
    const target = event.target as HTMLSelectElement;
    const selectedValue = target.value;
    if (row && typeof row === 'object') {
      row.classification = selectedValue;
      // Mark this row as modified so validator_reason input becomes enabled
      this.rowModifiedState[rowIndex] = true;
    }
  }

  // Check if the validator_reason input should be enabled for a specific row
  public isRowModified(rowIndex: number): boolean {
    return this.rowModifiedState[rowIndex] === true;
  }

  public onChangeValidationReason(row: Record<string, unknown>, rowIndex: number, value: string): void {
    if (row && typeof row === 'object') {
      row.validator_reason = value;
    }
  }

  public goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
    }
  }

  public goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
    }
  }

  public trackByRowIndex(index: number): number {
    return index;
  }

  public getRowKey(row: Record<string, unknown>): string {
    const key = this.accordionRowKey ? this.accordionRowKey : this.accordionKey;
    return String(row[key] ?? '');
  }

  public toggleAccordion(event: MouseEvent, row: Record<string, unknown>): void {
    event.stopPropagation();

    const rowKey = this.getRowKey(row);
    if (!rowKey) {
      return;
    }

    if (this.expandedRowKeys.has(rowKey)) {
      this.expandedRowKeys.delete(rowKey);
      return;
    }

    this.expandedRowKeys.add(rowKey);
  }

  public isExpanded(row: Record<string, unknown>): boolean {
    const rowKey = this.getRowKey(row);
    return rowKey !== '' && this.expandedRowKeys.has(rowKey);
  }

  public getChildRows(row: Record<string, unknown>): Record<string, unknown>[] {
    const rowKey = this.getRowKey(row);
    return this.childRowsByParentKey[rowKey] ?? [];
  }

  public getHeaderClass(column: DataTableColumn): string {
    if (column.key === 'revision_statement') {
      return 'revision-statement-header';
    }
    return '';
  }

  /** ------------------- View More and view less scenario ----------------------- */

  // Method to get the cell value (updated to handle undefined keys gracefully)
  getCellMoreValue(row: Record<string, unknown>, key: string | keyof Record<string, unknown>, rowIndex: number): string {
    const value = row[key];
    const cellKey = this.getCellKeyByIndex(rowIndex, key);
    const isExpanded = this.viewMoreState[cellKey] || false;

    if (value !== undefined && value !== null) {
      const stringValue = String(value);
      if (isExpanded) {
        return stringValue;
      }
      const truncatedValue = stringValue.length > 50 ? stringValue.slice(0, 50) + '...' : stringValue;
      return truncatedValue;
    }

    return '';
  }

  // Method to check if "View More" is active for a specific cell in a specific row
  isViewMore(row: Record<string, unknown>, key: string, rowIndex: number): boolean {
    const cellKey = this.getCellKeyByIndex(rowIndex, key);
    return this.viewMoreState[cellKey] || false;
  }


  // Method to toggle the "View More" state for a specific cell in a specific row
  toggleViewMore(row: Record<string, unknown>, key: string | keyof Record<string, unknown>, rowIndex: number): void {
    const cellKey = this.getCellKeyByIndex(rowIndex, key);

    // Reset all other fields for THIS specific row to false (only classification_reason for this row)
    Object.keys(this.viewMoreState).forEach((stateKey) => {
      if (stateKey.startsWith(`row_${rowIndex}_`) && stateKey !== cellKey) {
        this.viewMoreState[stateKey] = false;
      }
    });

    // Toggle the state for the selected cell
    this.viewMoreState[cellKey] = !this.viewMoreState[cellKey];
  }


  // Helper method to generate a unique key using ROW INDEX (not row id)
  getCellKeyByIndex(rowIndex: number, key: string): string {
    return `row_${rowIndex}_${key}`;
  }

  // Keep old method for backward compatibility
  getCellKey(row: Record<string, unknown>, key: string): string {
    const rowKey = row['id'] ?? row['statementId'] ?? Math.random();
    return `${JSON.stringify(rowKey)}_${key}`;
  }


  // Method to display the cell content based on the state for a specific row
  displayCellContent(row: Record<string, unknown>, key: string | keyof Record<string, unknown>, rowIndex: number): string {
    const value = String(this.getCellValue(row, key));
    if (this.isViewMore(row, key, rowIndex)) {
      return value; // Full content
    } else {
      return value.length > 50 ? value.slice(0, 50) + '...' : value; // Truncated content
    }
  }

}

